import { APP_BASE_HREF, CommonModule, DatePipe, DecimalPipe } from "@angular/common";
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from "@angular/common/http";
import { ApplicationConfig, importProvidersFrom, Injector, LOCALE_ID } from "@angular/core";
import { BrowserModule } from "@angular/platform-browser";
import { provideAnimations } from "@angular/platform-browser/animations";
import { provideRouter, Router } from "@angular/router";
import { MissingTranslationHandler, TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { OKTA_CONFIG, OktaAuthModule } from "@okta/okta-angular";
import OktaAuth from "@okta/okta-auth-js";
import { provideUserIdleConfig } from "angular-user-idle";
import { providePrimeNG } from 'primeng/config';
import { ConfirmationService, MessageService } from "primeng/api";
import Aura from '@primeuix/themes/aura';
import { environment } from "src/environments/environment";
import { routes } from "./app.routes";
import { ErrorInterceptor } from "./core/interceptors/error.interceptor";
import { CustomizedMissingTranslationHandler } from "./core/services/missing-translation-handler";

const DEFAULT_ORIGINAL_URI = window.location.origin;

function createTranslateLoader(http: HttpClient) {
    return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export const appConfig: ApplicationConfig = {
    providers: [
        providePrimeNG({
            theme: {
                preset: Aura,
                options: {
                    prefix: 'p',
                    darkModeSelector: 'system',
                    cssLayer: false,
                    cssVariables: true
                }
            }
        }),
        provideRouter(routes),
        provideHttpClient(withInterceptorsFromDi()),
        importProvidersFrom(
            CommonModule,
            BrowserModule,
            OktaAuthModule,
            TranslateModule.forRoot({
                loader: {
                    provide: TranslateLoader,
                    useFactory: createTranslateLoader,
                    deps: [HttpClient]
                },
                missingTranslationHandler: { provide: MissingTranslationHandler, useClass: CustomizedMissingTranslationHandler },
                defaultLanguage: 'es'
            })),
        DatePipe,
        DecimalPipe,
        MessageService,
        // { provide: HTTP_INTERCEPTORS, useClass: JwtInterceptor, multi: true },
        {
            provide: OKTA_CONFIG,
            useFactory: () => {
                const oktaAuth = new OktaAuth({
                    clientId: environment.oktaClientId,
                    issuer: environment.oktaIssuer,
                    //issuer: 'https://experian-spainb-dev.okta-emea.com/oauth2/default1',
                    //issuer: 'https://experian-spainb-dev.okta-emea.com/oauth2/aus1t9v3h4qZhJoId0i7',
                    // redirectUri: 'https://devopendataweb.experian.es/login/callback',
                    // redirectUri: 'https://localhost:4200/login/callback',
                    redirectUri: DEFAULT_ORIGINAL_URI + '/login/callback',
                    scopes: ['openid', 'profile', 'email'],
                    pkce: true,
                    //TODO: revisar
                    tokenManager: {
                        expireEarlySeconds: 10,
                        autoRenew: false,
                        storage: 'memory'
                    }
                });
                return {
                    oktaAuth,
                    onAuthRequired: (oktaAuth: OktaAuth, injector: Injector) => {
                        const triggerLogin = () => {
                            //await oktaAuth.signInWithRedirect();
                            // Redirect the user to your custom login page
                            const router = injector.get(Router);
                            void router.navigate(['/login']);
                        };
                        if (!oktaAuth.authStateManager.getPreviousAuthState()?.isAuthenticated) {
                            // App initialization stage
                            void triggerLogin();
                        }
                        else {
                            void triggerLogin();
                            // Ask the user to trigger the login process during token autoRenew process
                            //const modalService = injector.get(SuiModalService);
                            //modalService
                            //  .open(new ConfirmModal("Do you want to re-authenticate?", "Auth required", "Yes", "No"))
                            //  .onApprove(triggerLogin)
                            //  .onDeny(() => { });
                        }
                    }
                };
            }
        },
        { provide: APP_BASE_HREF, useValue: '/' },
        { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
        // Optionally you can set time for `idle`, `timeout` and `ping` in seconds.
        // Default values: `idle` is 600 (10 minutes), `timeout` is 300 (5 minutes)
        // and `ping` is 120 (2 minutes).
        provideUserIdleConfig({ idle: environment.idleSecondsTimeout, timeout: 1, ping: 120 }),
        ConfirmationService,
        { provide: LOCALE_ID, useValue: 'es-ES' },
        provideAnimations()
    ]
};
